import { fileDownload } from 'utils/fileDownload';
import { logError } from 'utils/sentryUtils';

import { mmFetch } from './mmFetch';
import { mmFetchWithResponse } from './mmFetchWithResponse';

const buildFetchQuery = (key, params = {}) => {
    const searchParams = new URLSearchParams();
    searchParams.set('key', key);

    Object.entries(params).forEach(([paramKey, value]) => {
        if (value === undefined || value === null || value === '') {
            return;
        }

        if (Array.isArray(value)) {
            value.forEach((item) => searchParams.append(`${paramKey}[]`, item));
            return;
        }

        searchParams.set(paramKey, value);
    });

    return searchParams.toString();
};

class PackagingPurchaseOrderV2Api {
    constructor() {
        this.baseUrl = 'api/warehouse/v2/packaging/purchaseOrders';
    }

    // eslint-disable-next-line no-unused-vars
    async bulkUpload(file, _params) {
        const respObj = { data: {}, msg: '', status: false };

        try {
            const formData = new FormData();
            formData.append('file', file);

            const url = `${this.baseUrl}/bulk/upload`;
            const response = await mmFetch(url, {
                body: formData,
                headers: {}, // Let browser set Content-Type for multipart
                method: 'POST',
            });

            if (response.status === 200) {
                const decodedResp = await response.json();
                respObj.status = true;
                respObj.msg = decodedResp.msg;
                respObj.data = decodedResp.data;
            } else {
                try {
                    const decodedResp = await response.json();
                    respObj.msg = decodedResp.msg || 'Upload failed';
                } catch (error) {
                    console.error('Error decoding response:', error);
                    respObj.msg = `Upload failed with status ${response.status}`;
                }
            }
        } catch (e) {
            // Re-throw AbortError so TanStack Query can handle cancellation
            if (e.name === 'AbortError') {
                throw e;
            }
            respObj.msg = `Upload error: ${e.message}`;
        }

        return respObj;
    }

    async bulkFetch(key, params, signal) {
        const query = buildFetchQuery(key, params ?? {});
        const url = `${this.baseUrl}/bulk/fetch?${query}`;
        return mmFetchWithResponse(url, { signal });
    }

    async bulkEdit(key, params, rowsToUpdate, rowsToDelete) {
        const url = `${this.baseUrl}/bulk/edit`;
        return mmFetchWithResponse(url, {
            body: JSON.stringify({
                key,
                params: params ?? {},
                rowsToDelete,
                rowsToUpdate,
            }),
            method: 'PUT',
        });
    }

    async bulkPersist(key, params) {
        const url = `${this.baseUrl}/bulk/persist`;
        return mmFetchWithResponse(url, {
            body: JSON.stringify({
                key,
                params: params ?? {},
            }),
            method: 'PUT',
        });
    }

    /**
     * GET bulk/prePopulatedCsv — download a pre-populated CSV for the given packaging PO IDs.
     * @param {number[]} poIDs
     * @returns {Promise<{ status: boolean, msg?: string }>}
     */
    async downloadPrePopulatedCsv(poIDs) {
        const filename = 'pre-populated-packaging-PO-template.csv';
        if (!Array.isArray(poIDs) || poIDs.length === 0) {
            return { status: false, msg: 'Must provide at least one PO ID' };
        }
        const searchParams = new URLSearchParams();
        poIDs.forEach((id) => searchParams.append('poIDs[]', String(id)));
        const url = `${this.baseUrl}/bulk/prePopulatedCsv?${searchParams.toString()}`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
                return { status: true };
            }
            let errorMessage = 'Failed to download CSV.';
            try {
                const payload = await res.json();
                if (payload?.msg) {
                    errorMessage = payload.msg;
                }
            } catch (err) {
                // Ignore JSON parsing failures.
            }
            return { status: false, msg: errorMessage };
        } catch (e) {
            logError(e);
            return { status: false, msg: e?.message ?? 'Failed to download CSV.' };
        }
    }
}

export const packagingPurchaseOrderV2Api = new PackagingPurchaseOrderV2Api();
